import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Attach token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pollpak_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// =========================================================================
// SWR auto-cache layer
// -------------------------------------------------------------------------
// Every successful GET response is mirrored into localStorage so the NEXT
// component that asks for the same URL can paint from cache instantly on
// refresh AND on the next login (cache is scoped by stable user.id, not by
// volatile JWT — so logging in tomorrow still hydrates from yesterday's
// snapshot). Anonymous responses are stored under the "anon" scope.
//
// `clearApiCache()` is exported for admin "wipe browser data" actions but
// is no longer wired to logout — keeping the cache lets the same user
// experience instant paint on every subsequent login.
// =========================================================================
const CACHE_NS = "oodel_api_v1::";

// Endpoints that return the SAME data for every user. They should be
// cached globally (not user-scoped) so a fresh login on the same browser
// paints the admin's current values on the first frame instead of falling
// through to hardcoded fallbacks.
const GLOBAL_PATHS = [
  "/site-content",
  "/sectors",
  "/admin/profile-dna/public",
  "/results/public",
  "/cms/public",
  "/legal/",
  "/sentiment/items",
  "/sentiment/categories",
  "/sentiment/feature",
  "/articles",
];

function isGlobalPath(url) {
  return GLOBAL_PATHS.some((p) => url === p || url.startsWith(p + "/") || url.startsWith(p + "?"));
}

function userScope() {
  try {
    const raw = localStorage.getItem("pollpak_user_cache");
    if (raw) {
      const u = JSON.parse(raw);
      if (u && u.id) return `u:${u.id}`;
    }
  } catch { /* ignore */ }
  return "anon";
}

function cacheKey(path) {
  // Global endpoints share one bucket across all users — no scope.
  if (isGlobalPath(path)) return `${CACHE_NS}global::${path}`;
  return `${CACHE_NS}${userScope()}::${path}`;
}

api.interceptors.response.use((res) => {
  try {
    const method = (res.config?.method || "get").toLowerCase();
    const url = res.config?.url || "";
    if (method === "get" && res.status >= 200 && res.status < 300) {
      // Only cache JSON-ish bodies; skip giant blobs to avoid quota errors.
      if (url && !url.startsWith("http")) {
        const payload = JSON.stringify({ v: res.data, t: Date.now() });
        if (payload.length < 200_000) {
          localStorage.setItem(cacheKey(url), payload);
        }
        // PHASE 8.44 — also update the in-memory boot snapshot for the
        // three pre-fetched endpoints so subsequent SPA navigations paint
        // with the FRESH value (not the stale snapshot the inline script
        // captured at page load).
        const bootKey = BOOT_KEY_MAP[url];
        if (bootKey && typeof window !== "undefined" && window.__OODEL_BOOT__) {
          window.__OODEL_BOOT__[bootKey] = res.data;
        }
      }
    } else if (method !== "get" && res.status >= 200 && res.status < 300 && url) {
      // PHASE 8.44 — Any successful mutation against the admin CMS surface
      // invalidates BOTH the boot snapshot and the localStorage cache row
      // for the affected public read endpoint. Without this, an admin who
      // saves a label edit and then navigates to the public page would
      // still see the pre-save value paint for one frame (because the
      // useState initial would seed from the stale window.__OODEL_BOOT__).
      const invalidations = [];
      if (url.startsWith("/admin/cms") || url.startsWith("/admin/site-content") || url === "/admin/labels" || url.startsWith("/admin/labels/")) {
        invalidations.push("/site-content");
      }
      if (url.startsWith("/admin/profile-dna")) {
        invalidations.push("/admin/profile-dna/public");
      }
      if (url.startsWith("/admin/sectors")) {
        invalidations.push("/sectors");
      }
      for (const p of invalidations) {
        try { localStorage.removeItem(cacheKey(p)); } catch { /* swallow */ }
        const bootKey = BOOT_KEY_MAP[p];
        if (bootKey && typeof window !== "undefined" && window.__OODEL_BOOT__) {
          // Don't delete — set to null so getCached falls through to the
          // network rather than to a stale localStorage row.
          window.__OODEL_BOOT__[bootKey] = null;
        }
      }
    }
  } catch { /* quota / disabled storage — non-fatal */ }
  return res;
}, (err) => {
  // PHASE 8.43 — Global 401 handler. When a token is expired or revoked
  // (logout on another tab, password change, admin-forced session reset),
  // the backend returns 401. Clear local auth state and bounce to /login
  // EXCEPT when the failing call IS the login/auth endpoint itself
  // (otherwise wrong-password attempts would trigger a redirect loop).
  try {
    const status = err?.response?.status;
    const cfgUrl = (err?.config?.url || "").toString();
    const isAuthEndpoint = cfgUrl.startsWith("/auth/") || cfgUrl.includes("/auth/");
    // ITER 81 — a change-password call rotates the token (old one revoked,
    // fresh one saved to localStorage) but background pollers (Admin
    // layout's health/quality-issues/growth interval timers, etc.) may
    // have an in-flight request still carrying the OLD token. If that
    // request 401s AFTER the new token has already been saved, this
    // handler would otherwise wipe out the fresh token and force-redirect
    // — even though the session is perfectly valid. Guard: only treat
    // this as a real session expiry if the token this request actually
    // used still matches what's CURRENTLY in localStorage (i.e. nothing
    // rotated it out from under this specific failed call).
    const failedAuthHeader = err?.config?.headers?.Authorization || "";
    const failedToken = failedAuthHeader.startsWith("Bearer ") ? failedAuthHeader.slice(7) : "";
    const currentToken = typeof window !== "undefined" ? localStorage.getItem("pollpak_token") : null;
    const tokenWasRotatedAway = !!failedToken && !!currentToken && failedToken !== currentToken;
    // ITER 106 — a 401 only means "your session expired" if there WAS a
    // session to begin with. Anonymous visitors calling a best-effort,
    // auth-optional endpoint (e.g. the language-preference PUT fired from
    // the public Trackers/Snapshots pages) never attach a token, so the
    // backend correctly 401s — but that's not a session expiry, it's just
    // "you were never logged in". Without this guard the interceptor would
    // force-redirect a logged-out visitor away from whatever public page
    // they were innocently interacting with.
    const hadSession = !!failedToken || !!currentToken;
    if (status === 401 && !isAuthEndpoint && !tokenWasRotatedAway && hadSession && typeof window !== "undefined") {
      localStorage.removeItem("pollpak_token");
      // Wipe cached per-user GET responses so a re-login pulls fresh data.
      try {
        const keysToWipe = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(CACHE_NS) && !k.startsWith(CACHE_NS + "global::")) keysToWipe.push(k);
        }
        keysToWipe.forEach((k) => localStorage.removeItem(k));
      } catch { /* swallow */ }
      // Only redirect if we're not already on /login (avoid loop)
      if (!window.location.pathname.startsWith("/login")) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}&reason=session_expired`);
      }
    }
  } catch { /* never let interceptor swallow the original error */ }
  return Promise.reject(err);
});

// Synchronous cache read — components call this from their useState
// initializer. Returns the previously-seen response, or null if none.
//
// PHASE 8.44 — Boot pre-fetch fallback. `public/index.html` pre-fetches
// `/site-content`, `/admin/profile-dna/public`, and `/sectors` before
// React boots. When a component asks for one of those endpoints we
// return the boot data first (it's strictly fresher than any localStorage
// row). This kills the "old text flashes for one frame, then snaps to
// the new admin-edited text" pattern across the entire CMS surface.
const BOOT_KEY_MAP = {
  "/site-content": "site_content",
  "/admin/profile-dna/public": "profile_dna",
  "/sectors": "sectors",
};
api.getCached = function getCached(path) {
  try {
    const bootKey = BOOT_KEY_MAP[path];
    if (bootKey && typeof window !== "undefined" && window.__OODEL_BOOT__ && window.__OODEL_BOOT__[bootKey]) {
      return window.__OODEL_BOOT__[bootKey];
    }
    const raw = localStorage.getItem(cacheKey(path));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && "v" in obj ? obj.v : null;
  } catch {
    return null;
  }
};

// Hard wipe — called from auth.logout(). Removes every cached GET row
// (current scope AND any orphaned rows from previous JWTs on this browser).
export function clearApiCache() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      // Keep the `global::` rows — they're shared across users (CMS,
      // sectors, profile-DNA, public results) and discarding them would
      // re-introduce the "City flashes briefly then turns into Country"
      // flicker on every fresh login.
      if (k && k.startsWith(CACHE_NS) && !k.startsWith(CACHE_NS + "global::")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* non-fatal */ }
}

export default api;

// window.open()-after-await gets blocked as a popup by most browsers (the
// user-gesture context is lost once an async network round-trip happens
// first). Triggering a native `<a download>` click is exempt from that
// restriction — this matches the existing invoice/report PDF pattern
// already used elsewhere in this codebase (BusinessDashboard.jsx,
// AdminCustomerSentiment.jsx), just centralized here for the Documents module.
export function openPdfBlob(path, filename = "document.pdf") {
  return api.get(path, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  });
}

export function fmtError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join("; ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
